import { getApiUrl, getLocalStorage, asyncRefreshTokenIfRequired } from '../components/login/utils.js'
import { createApi } from '@reduxjs/toolkit/query/react'
import axios from 'axios'
import { Endpoints } from '../components/constants/Endpoints'
import { h3ToFeature } from 'geojson2h3'
import { ClassificationColors } from '../components/constants/Colors'
import { LocalStorage } from '../components/constants/LocalStorage.js'
import { Status } from '../components/JobHelpers.js'
import { addJobs } from '../reducers/jobs.js'
import { removeMeasurement } from '../reducers/measurements.js'

// Create an axios instance and add a request interceptor to the axios instance.
// This way the accessToken is refreshed before each request.
const axiosInstance = axios.create()
axiosInstance.interceptors.request.use(async (configuration) => {
  await asyncRefreshTokenIfRequired()

  // Set the Authorization header
  const accessToken = getLocalStorage(LocalStorage.AccessToken)
  configuration.headers.Authorization = `Bearer ${accessToken}`

  return configuration
}, (error) => {
  return Promise.reject(error)
})

/**
 * Base query which uses axios under the hood.
 * https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#axios-basequery
 *
 * This uses `axiosInstance` to refresh the access token before the request if required.
 */
const baseQuery =
  ({ baseUrl } = { baseUrl: '' }) =>
    async ({ url, method, headers, params, body }) => {
      try {
        const result = await axiosInstance({
          url: baseUrl + url, method, headers, params, data: body
        })
        return { data: result.data }
        // To return the location header use the following (from RadSim app)
        // See https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#adding-meta-information-to-queries
        // return { data: result.data, meta: { headers: result.headers } }
        // And in the apiSlice below:
        // Only returning location header as the full header is not serializable
        // return { data, headers: { location: meta.headers.location } }
      } catch (axiosError) {
        return { error: axiosError }
      }
    }

/**
 * Define a service using a base URL and expected endpoints
 *
 * Allows to define a set of endpoints that describe how to retrieve data from a series of
 * endpoints, including configuration of how to fetch and transform that data
 */
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQuery({ baseUrl: getApiUrl() }),
  tagTypes: ['SegmentHistory'],
  endpoints: (builder) => ({
    getUser: builder.mutation({
      query: (userName) => ({
        url: Endpoints.Users(userName),
        method: 'GET'
      })
    }),
    deleteH3: builder.mutation({
      query: (userName) => ({
        url: Endpoints.Users(userName) + '/' + Endpoints.H3,
        method: 'DELETE'
      })
    }),
    deleteSegments: builder.mutation({
      query: (userName) => ({
        url: Endpoints.Users(userName) + '/' + Endpoints.Segments,
        method: 'DELETE'
      })
    }),
    // Untested api
    updateMeasurement: builder.mutation({
      query: ({ item, body }) => ({
        url: Endpoints.Measurements + '/' + item.deviceId + '/' + item.measurementId,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json-patch+json'
          // The Authorization header is injected by the axiosInstance interceptor
        },
        body
      })
    }),
    // Untested api
    updateFinishedJob: builder.mutation({
      query: ({ jobid, body }) => ({
        url: Endpoints.FinishedJobs + '/' + jobid,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json-patch+json'
          // The Authorization header is injected by the axiosInstance interceptor
        },
        body
      })
    }),
    createJob: builder.mutation({
      query: (body) => ({
        url: Endpoints.Job,
        method: 'POST',
        body
        // The Authorization header is injected by the axiosInstance interceptor
      })
    }),
    deleteMeasurement: builder.mutation({
      query: (dataset) => ({
        url: `${Endpoints.Measurements}/${dataset.deviceId}/${dataset.measurementId}`,
        method: 'DELETE'
        // The Authorization header is injected by the axiosInstance interceptor
      }),
      onQueryStarted: async (dataset, { dispatch, queryFulfilled }, enqueueSnackbar) => {
        const result = await queryFulfilled
        if (result.data.status === Status.Finished) {
          const deletionJob = result.data
          dispatch(addJobs([deletionJob]))
          dispatch(removeMeasurement(dataset.id))
        } else if (result.data.status === Status.Failed) {
          enqueueSnackbar('Daten konnten nicht gelöscht werden. Bitte kontaktieren Sie den ' +
              'Support falls der Fehler wiederholt auftritt.')
        }
      }
    }),
    getSegments: builder.query({
      query: () => ({
        url: Endpoints.Segments,
        method: 'get'
        // The Authorization header is injected by the axiosInstance interceptor
      }),
      // each call to this endpoint refetches (i.e. not using cache)
      forceRefetch: (args) => true,
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName
      },
      invalidatesTags: ['SegmentHistory'],
      transformResponse: (response) => {
        const geoJson = response
        geoJson.features.forEach(f => {
          f.id = Math.round(Math.random() * 100000000)
        })
        return geoJson
      }
    }),
    getSegmentHistory: builder.query({
      query: (properties) => ({
        url: Endpoints.Segments + '/' + properties.oid + '/history',
        method: 'get'
        // The Authorization header is injected by the axiosInstance interceptor
      }),
      providesTags: ['SegmentHistory']
    }),
    getH3: builder.query({
      query: () => ({
        url: Endpoints.H3,
        method: 'get'
        // The Authorization header is injected by the axiosInstance interceptor
      }),
      // each call to this endpoint refetches (i.e. not using cache)
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName
      },
      forceRefetch: (arg) => {
        return true
      },
      transformResponse: (response) => {
        const geoJson = response
        const maxDataSizeForOverview = 2000
        // Due to the floor, max size is actually between 2000-3999
        const filterSize = geoJson.features.length > maxDataSizeForOverview
          ? Math.floor(geoJson.features.length / maxDataSizeForOverview)
          : 1
        const features = geoJson.features.map((geometryLessFeature, geometryLessFeatureIndex) => {
          const properties = geometryLessFeature.properties
          const hash = properties.h3_hash
          const feature = h3ToFeature(hash, properties)
          const meanNinetyPercentile = properties.mean_ninety_percentile

          // Keep this classification mapping in sync with provider/PointsConverter (h3 export)
          feature.properties.color = meanNinetyPercentile < 2
            ? ClassificationColors.BLUE
            : meanNinetyPercentile < 4
              ? ClassificationColors.GREEN
              : meanNinetyPercentile < 6
                ? ClassificationColors.YELLOW
                : ClassificationColors.RED
          // Only show overview for every nth feature
          if (!(geometryLessFeatureIndex % filterSize)) {
            feature.properties.overview = true
          }
          return feature
        })
        geoJson.features = features
        return geoJson
      }
    }),
    getMeasurements: builder.query({
      query: () => ({
        url: 'measurements',
        method: 'get'
        // The Authorization header is injected by the axiosInstance interceptor
      }),
      // each call to this endpoint refetches (i.e. not using cache)
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName
      },
      forceRefetch: (arg) => {
        return true
      },
      transformResponse: (retValue) => {
        const features = retValue.features
        const deviceData = new Map()
        features.forEach(f => {
          const metadata = f.properties.metadata
          const measurementId = parseInt(metadata.measurementId)
          const deviceId = metadata.deviceId
          if (!deviceData.has(deviceId)) {
            deviceData.set(deviceId, new Map())
          }

          // We don't show all measurements on the map, yet, we don't need the GeoJson and,
          // thus, we map the features to normal objects.
          const measurement = {
            id: metadata.deviceId + ':' + measurementId,
            tags: f.tags,
            oid: f.properties.oid,
            geometry: f.geometry,
            length: metadata.length, // length of the measurement path
            size: f.properties.length, // size of the raw data in bytes
            modality: metadata.modality,
            deserialized: f.properties.deserialized,
            startTimestamp: metadata.startTimestamp,
            endTimestamp: metadata.endTimestamp,
            jobs: [],
            deviceId: metadata.deviceId,
            measurementId,
            user: f.properties.username,
            uploadDate: f.properties.uploadDate,
            osVersion: metadata.osVersion,
            deviceType: metadata.deviceType,
            appVersion: metadata.appVersion,
            formatVersion: metadata.formatVersion
          }
          deviceData.get(deviceId).set(measurementId, measurement)
        })

        const measurements = []
        deviceData.forEach((v, k) => {
          v.forEach((m, mid) => measurements.push(m))
        })

        // Sort measurements
        measurements.sort((a, b) => {
          return a.startTimestamp !== undefined && b.startTimestamp !== undefined
            ? b.startTimestamp - a.startTimestamp
            : new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime()
        })
        return measurements
      }
    }),
    getGeoLocations: builder.query({
      query: (dataset) => ({
        url: Endpoints.Locations,
        method: 'get',
        headers: {
          deviceId: dataset.deviceId,
          measurementId: dataset.measurementId
          // The Authorization header is injected by the axiosInstance interceptor
        }
      })
    }),
    getImage: builder.query({
      query: (imageId) => ({
        url: Endpoints.Images + '/' + imageId,
        method: 'get'
        // The Authorization header is injected by the axiosInstance interceptor
      })
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSegmentHistoryQuery, useGetImageQuery, useUpdateFinishedJobMutation
} = apiSlice
